/**
 * The patient's Prism Labs phone body scans on the clinic patient page (Labs tab), trended month to month next to the
 * check-ins (Reference ID sheet v12 "Read me" item 5; microdose workbook v1.2 OPT-ELIG-010, tab 09 and tab 12).
 *
 * The baseline (the first COMPLETE scan) is marked and a retake never moves it (DEV-BG-05). A PARTIAL or FAILED scan
 * is listed with its reason. A measure Prism did not return reads "Not returned": nothing is estimated or carried over
 * (DEV-BG-03). Measurements only: no score, band or chance of anything. Read only, for every clinic role; the API
 * checks the role and records the read.
 *
 * The reminder line says what the daily run will really do for the latest check-in (the API works it out with the
 * same rule, the patient's text consent, phone and Prism details). While scans are switched off there is no "Scan
 * due" and no reminder line. When lean mass fell on each of the last two complete scans, the panel says so (the one
 * reading of OPT-HE-006's "sustained drop across 2 in-portal body scans" that needs no new number), as an observation.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatDate } from '../lib/format';
import { EmptyState, ErrorState, InfoHint, LoadingRows } from './ui';
import { IconAlert, IconInfo } from './icons';
import { BodyScanChart, formatMeasure, type ScanPoint } from './BodyScanChart';

/* ------------------------------------------------------------------ *
 * What the API sends (GET /body-scan/patients/:id)
 * ------------------------------------------------------------------ */

type MetricKey =
  | 'bodyFatPct'
  | 'leanMassLb'
  | 'skeletalMuscleMassLb'
  | 'waistIn'
  | 'hipIn'
  | 'fatMassLb'
  | 'visceralFatMassLb'
  | 'appendicularLeanMassLb';
type Values = Record<MetricKey, number | null>;
type ScanState = 'PENDING' | 'PROCESSING' | 'COMPLETE' | 'PARTIAL' | 'FAILED' | 'EXPIRED';

interface ScanRow {
  id: string;
  takenAt: string;
  state: ScanState;
  detail: string | null;
  device: string;
  isBaseline: boolean;
  isRetake: boolean;
  values: Values | null;
  sinceBaseline: Values | null;
}

interface TrendRow {
  month: number | null;
  checkinAt: string | null;
  scans: ScanRow[];
  awaitingScan: boolean;
  reminder: { status: string; at: string; reason: string | null } | null;
  /** Latest check-in only: the reminder still to come, or why there will be none. */
  reminderPlan: { kind: 'planned'; at: string } | { kind: 'not_planned'; reason: string } | null;
}

interface BodyScanView {
  enabled: boolean;
  scanDue: boolean;
  reminderDays: number;
  missingForPrism: string[];
  consent: { onFile: boolean; acceptedAt: string | null; termsVersion: string | null; researchConsent: boolean } | null;
  baselineScanId: string | null;
  baselineAt: string | null;
  rows: TrendRow[];
  series: Record<MetricKey, ScanPoint[]>;
  checkins: Array<{ month: number; at: string }>;
  returnedCounts: Record<MetricKey, number>;
  leanMassLowerTwice: { scans: Array<{ scanId: string; takenAt: string; leanMassLb: number }> } | null;
}

const METRICS: Array<{ key: MetricKey; label: string; short: string; unit: string }> = [
  { key: 'bodyFatPct', label: 'Body fat', short: 'Body fat', unit: '%' },
  { key: 'leanMassLb', label: 'Lean mass', short: 'Lean mass', unit: 'lb' },
  { key: 'skeletalMuscleMassLb', label: 'Skeletal muscle', short: 'Skeletal muscle', unit: 'lb' },
  { key: 'waistIn', label: 'Waist', short: 'Waist', unit: 'in' },
  { key: 'hipIn', label: 'Hips', short: 'Hips', unit: 'in' },
  { key: 'fatMassLb', label: 'Fat mass', short: 'Fat mass', unit: 'lb' },
  { key: 'visceralFatMassLb', label: 'Visceral fat mass', short: 'Visceral fat', unit: 'lb' },
  { key: 'appendicularLeanMassLb', label: 'Appendicular lean mass', short: 'Appendicular lean', unit: 'lb' },
];

/** The table shows these; the rest are in the chart's measure picker. */
const TABLE_METRICS = METRICS.slice(0, 7);

const STATE: Record<ScanState, { label: string; className: string }> = {
  COMPLETE: { label: 'Complete', className: 'bg-mint text-forest' },
  PARTIAL: { label: 'Partial', className: 'bg-risk-yellow-soft text-risk-yellow' },
  FAILED: { label: 'Failed', className: 'bg-alert-soft text-alert' },
  PROCESSING: { label: 'Processing', className: 'bg-info-soft text-navy' },
  PENDING: { label: 'Sent to Prism', className: 'bg-info-soft text-navy' },
  // Never listed (the API leaves out starts that were never captured); here so every state has a label.
  EXPIRED: { label: 'Not finished', className: 'bg-paper-deep text-ink-muted' },
};

/** A status: a pill with a dot, so it never reads as a control and never relies on colour alone. */
function StatusBadge({ className, children }: { className: string; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-[4px] text-[11.5px] font-semibold leading-none ${className}`}>
      <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {children}
    </span>
  );
}

/** A plain tag on a scan (Baseline, Retake): square corners, no dot, so it reads as a label. */
function Tag({ children, tone = 'plain' }: { children: ReactNode; tone?: 'plain' | 'forest' }) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-[4px] px-1.5 py-[3px] text-[10.5px] font-semibold uppercase leading-none tracking-[0.04em] ${
        tone === 'forest' ? 'bg-forest text-white' : 'bg-paper-deep text-ink-muted'
      }`}
    >
      {children}
    </span>
  );
}

function signed(n: number, unit: string): string {
  const abs = formatMeasure(Math.abs(n), unit === '%' ? 'pts' : unit).replace('%', '');
  return n === 0 ? 'No change' : `${n > 0 ? '+' : '-'}${abs}`;
}

const monthLabel = (month: number | null) => (month === null ? 'Intake' : `Month ${month}`);

/** "No SMS consent on file" reads "no SMS consent on file" after a colon. */
const lowerFirst = (text: string) => (/^[A-Z][a-z]/.test(text) ? text[0]!.toLowerCase() + text.slice(1) : text);

/**
 * What happened, or will happen, with the reminder text for one check-in. Only what the API reports: a logged send,
 * failure or skip, and for the latest check-in the plan worked out with the daily run's own rule. Nothing while the
 * scan is switched off.
 */
function reminderLine(row: TrendRow, enabled: boolean): string | null {
  if (!enabled || row.month === null || !row.awaitingScan) return null;
  const r = row.reminder;
  const plan = row.reminderPlan;
  if (r?.status === 'SENT') return `Reminder texted ${formatDate(r.at)}`;
  if (r?.status === 'FAILED') return `Reminder not delivered: ${lowerFirst(r.reason ?? 'the text failed')}`;
  if (plan?.kind === 'planned') {
    return new Date(plan.at).getTime() > Date.now() ? `One reminder text on ${formatDate(plan.at)} if no scan by then` : 'Reminder text goes out with the next daily run';
  }
  if (r?.status === 'SKIPPED_OPT_OUT') return `No reminder texted: ${lowerFirst(r.reason ?? 'texting not allowed')}`;
  if (plan?.kind === 'not_planned') return `No reminder text: ${lowerFirst(plan.reason)}`;
  return null;
}

/* ------------------------------------------------------------------ *
 * The panel
 * ------------------------------------------------------------------ */

export function BodyScanPanel({ patientId }: { patientId: string }) {
  const query = useQuery({
    queryKey: ['body-scan', patientId],
    queryFn: () => api.get<BodyScanView>(`/body-scan/patients/${patientId}`),
  });
  const data = query.data;
  const offered = useMemo(() => METRICS.filter((m) => (data?.returnedCounts[m.key] ?? 0) > 0), [data]);
  const [picked, setPicked] = useState<MetricKey>('bodyFatPct');
  const metric = offered.find((m) => m.key === picked) ?? offered[0] ?? null;
  const scanCount = data?.rows.reduce((n, r) => n + r.scans.length, 0) ?? 0;

  return (
    <section className="mt-3 rounded-[10px] border border-hairline bg-white px-4 py-4" data-testid="body-scan-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-display text-[20px] text-navy">Phone body scans</h3>
            <InfoHint label="About phone body scans">
              Scans the patient takes with their phone in the portal (Prism Labs), every month, for following the trend
              only. Confirm a lean-mass concern by DXA or InBody before changing the track. The baseline is the first
              complete scan and a retake never moves it. A measure Prism did not return is shown as not returned and is
              never estimated.
            </InfoHint>
          </div>
          <p className="mt-0.5 text-[13.5px] text-ink-muted">
            Prism Labs, month to month next to the check-ins
            {data?.baselineAt ? `. Baseline ${formatDate(data.baselineAt)}` : ''}
          </p>
        </div>
        {data && (data.enabled || scanCount > 0) && (
          <div className="flex flex-wrap items-center gap-2" data-testid="body-scan-status">
            {!data.enabled ? (
              <StatusBadge className="bg-paper-deep text-ink-muted">Scans switched off</StatusBadge>
            ) : data.scanDue ? (
              <StatusBadge className="bg-risk-yellow-soft text-risk-yellow">Scan due</StatusBadge>
            ) : scanCount > 0 ? (
              <StatusBadge className="bg-mint text-forest">Up to date</StatusBadge>
            ) : null}
            <StatusBadge className="bg-paper-deep text-ink-muted">
              {data.consent?.onFile ? `Consent ${formatDate(data.consent.acceptedAt)}` : 'No scan consent yet'}
            </StatusBadge>
          </div>
        )}
      </div>

      {query.isLoading ? (
        <div className="mt-3">
          <LoadingRows rows={4} />
        </div>
      ) : query.error || !data ? (
        <div className="mt-3">
          <ErrorState error={query.error} retry={() => void query.refetch()} />
        </div>
      ) : !data.enabled && scanCount === 0 ? (
        <EmptyState title="Phone body scans are not switched on yet" body="Once Prism Labs is set up for the clinic, this patient's monthly scans appear here next to their check-ins." />
      ) : (
        <>
          {data.enabled && data.missingForPrism.length > 0 && (
            <p className="mt-3 flex items-start gap-2 rounded-lg bg-risk-yellow-soft px-3 py-2 text-[13px] text-navy" data-testid="body-scan-missing">
              <span className="mt-[1px] shrink-0 text-risk-yellow">
                <IconAlert size={16} />
              </span>
              Prism needs this patient's {data.missingForPrism.join(', ')} before they can scan.
            </p>
          )}

          {data.rows.length === 0 ? (
            <EmptyState
              title="No phone scans yet"
              body={data.scanDue ? 'A scan is due. The patient sees a "Start my body scan" card in their portal.' : 'Scans the patient takes in their portal appear here.'}
            />
          ) : (
            <>
              {data.leanMassLowerTwice && <LeanMassNote pattern={data.leanMassLowerTwice} />}
              {scanCount === 0 && (
                <p className="mt-3 text-[13px] text-ink-muted" data-testid="body-scan-none">
                  No phone scans yet.{data.scanDue ? ' The patient sees a "Start my body scan" card in their portal.' : ''}
                </p>
              )}
              {metric && (
                <div className="mt-4">
                  <div className="-mx-1 mb-2 flex gap-1.5 overflow-x-auto px-1 pb-1" role="tablist" aria-label="Measure to chart">
                    {offered.map((m) => (
                      <button
                        key={m.key}
                        type="button"
                        role="tab"
                        aria-selected={m.key === metric.key}
                        onClick={() => setPicked(m.key)}
                        className={`shrink-0 rounded-control border px-3 py-1.5 text-xs font-semibold transition ${
                          m.key === metric.key
                            ? 'border-[var(--brand-accent)] bg-[var(--brand-accent)] text-white'
                            : 'border-hairline bg-white text-ink-muted hover:text-ink'
                        }`}
                      >
                        {m.short}
                      </button>
                    ))}
                  </div>
                  <BodyScanChart points={data.series[metric.key]} checkins={data.checkins} label={metric.label} unit={metric.unit} />
                  <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11.5px] text-ink-muted" aria-label="Chart key">
                    <li className="flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full bg-sage" aria-hidden="true" />
                      Scan
                    </li>
                    <li className="flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full border-2 border-sage" aria-hidden="true" />
                      Partial scan
                    </li>
                    <li className="flex items-center gap-1.5">
                      <span className="grid h-3.5 w-3.5 place-items-center rounded-full border-2 border-forest" aria-hidden="true">
                        <span className="h-1 w-1 rounded-full bg-forest" />
                      </span>
                      Baseline
                    </li>
                    <li className="flex items-center gap-1.5">
                      <span className="h-3 border-l border-dashed border-ink-muted" aria-hidden="true" />
                      Check-in
                    </li>
                  </ul>
                </div>
              )}

              {/* A phone gets one card per check-in; a wider screen gets the table. */}
              <div className="mt-4 space-y-2.5 sm:hidden" data-testid="body-scan-cards">
                {data.rows.map((row) => (
                  <MonthCard key={String(row.month)} row={row} enabled={data.enabled} />
                ))}
              </div>
              <div className="mt-4 hidden overflow-x-auto rounded-lg border border-hairline sm:block">
                <table className={`w-full text-[13px] ${scanCount > 0 ? 'min-w-[920px]' : ''}`} data-testid="body-scan-table">
                  <thead>
                    <tr className="border-b border-hairline bg-paper text-left">
                      <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-ink-muted">Check-in</th>
                      <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-ink-muted">Scan</th>
                      {(scanCount > 0 ? TABLE_METRICS : []).map((m) => (
                        <th key={m.key} className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
                          {m.short}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row) => (
                      <MonthRows key={String(row.month)} row={row} enabled={data.enabled} metrics={scanCount > 0 ? TABLE_METRICS : []} />
                    ))}
                  </tbody>
                </table>
              </div>
              {scanCount > 0 && <p className="mt-2 text-[12px] text-ink-muted">Changes are against the baseline. Phone scans are estimates, for trend only.</p>}
            </>
          )}
        </>
      )}
    </section>
  );
}

/** Lean mass fell on each of the last two complete scans: said plainly, with the values behind the hint. */
function LeanMassNote({ pattern }: { pattern: NonNullable<BodyScanView['leanMassLowerTwice']> }) {
  return (
    <p className="mt-3 flex items-start gap-2 rounded-lg bg-info-soft px-3 py-2 text-[13px] text-navy" data-testid="body-scan-lean-mass">
      <span className="mt-[1px] shrink-0 text-navy">
        <IconInfo size={16} />
      </span>
      <span className="min-w-0">
        Lean mass lower on each of the last 2 complete scans{' '}
        <InfoHint label="About the lean mass trend">
          {pattern.scans.map((s) => `${formatDate(s.takenAt)}: ${formatMeasure(s.leanMassLb, 'lb')}`).join('. ')}. Phone scans are estimates.
          Confirm a lean-mass decline by DXA or InBody before changing the track or the dose.
        </InfoHint>
      </span>
    </p>
  );
}

function MonthRows({ row, enabled, metrics }: { row: TrendRow; enabled: boolean; metrics: typeof TABLE_METRICS }) {
  const reminder = reminderLine(row, enabled);
  const head = (
    <td className="px-3 py-2.5 align-top" rowSpan={Math.max(1, row.scans.length)}>
      <span className="block font-semibold text-navy">{monthLabel(row.month)}</span>
      <span className="block text-[12px] text-ink-muted">{row.checkinAt ? formatDate(row.checkinAt) : row.month === null ? 'After the intake' : 'No check-in on file'}</span>
    </td>
  );

  if (row.scans.length === 0) {
    return (
      <tr className="border-b border-hairline last:border-0" data-testid={`body-scan-row-${row.month ?? 'intake'}`}>
        {head}
        <td className="px-3 py-2.5 align-top" colSpan={metrics.length + 1}>
          <span className="block text-navy">No scan yet</span>
          {reminder && <span className="block text-[12px] text-ink-muted">{reminder}</span>}
        </td>
      </tr>
    );
  }

  return (
    <>
      {row.scans.map((scan, index) => (
        <tr key={scan.id} className={`${index === row.scans.length - 1 ? 'border-b border-hairline last:border-0' : ''}`} data-testid={`body-scan-scan-${scan.id}`}>
          {index === 0 && head}
          <td className="px-3 py-2.5 align-top">
            <span className="flex flex-wrap items-center gap-1.5">
              <span className="whitespace-nowrap text-navy">{formatDate(scan.takenAt)}</span>
              <StatusBadge className={STATE[scan.state].className}>{STATE[scan.state].label}</StatusBadge>
              {scan.isBaseline && <Tag tone="forest">Baseline</Tag>}
              {scan.isRetake && <Tag>Retake</Tag>}
            </span>
            {scan.detail && (scan.state === 'PARTIAL' || scan.state === 'FAILED') && (
              <span className="mt-1 block max-w-[260px] text-[12px] text-ink-muted">{scan.detail}</span>
            )}
            {index === row.scans.length - 1 && reminder && <span className="mt-1 block text-[12px] text-ink-muted">{reminder}</span>}
          </td>
          {metrics.map((m) => {
            const value = scan.values?.[m.key] ?? null;
            const change = scan.sinceBaseline?.[m.key] ?? null;
            const measured = scan.state === 'COMPLETE' || scan.state === 'PARTIAL';
            return (
              <td key={m.key} className="whitespace-nowrap px-3 py-2.5 text-right align-top">
                {!measured ? (
                  <span className="text-ink-muted">-</span>
                ) : value === null ? (
                  <span className="text-[12px] italic text-ink-muted">Not returned</span>
                ) : (
                  <>
                    <span className="block text-navy">{formatMeasure(value, m.unit)}</span>
                    {change !== null && <span className="block text-[11.5px] text-ink-muted">{signed(change, m.unit)}</span>}
                  </>
                )}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

/** The same rows on a phone: each scan with its measures in two columns. */
function MonthCard({ row, enabled }: { row: TrendRow; enabled: boolean }) {
  const reminder = reminderLine(row, enabled);
  return (
    <div className="rounded-lg border border-hairline" data-testid={`body-scan-card-${row.month ?? 'intake'}`}>
      <div className="flex items-baseline justify-between gap-2 border-b border-hairline bg-paper px-3 py-2">
        <span className="font-semibold text-navy">{monthLabel(row.month)}</span>
        <span className="text-[12px] text-ink-muted">{row.checkinAt ? `Check-in ${formatDate(row.checkinAt)}` : row.month === null ? 'After the intake' : 'No check-in on file'}</span>
      </div>
      {row.scans.length === 0 ? (
        <div className="px-3 py-2.5 text-[13px]">
          <span className="block text-navy">No scan yet</span>
          {reminder && <span className="block text-[12px] text-ink-muted">{reminder}</span>}
        </div>
      ) : (
        <ul className="divide-y divide-hairline">
          {row.scans.map((scan, index) => {
            const measured = scan.state === 'COMPLETE' || scan.state === 'PARTIAL';
            return (
              <li key={scan.id} className="px-3 py-2.5 text-[13px]">
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="text-navy">{formatDate(scan.takenAt)}</span>
                  <StatusBadge className={STATE[scan.state].className}>{STATE[scan.state].label}</StatusBadge>
                  {scan.isBaseline && <Tag tone="forest">Baseline</Tag>}
                  {scan.isRetake && <Tag>Retake</Tag>}
                </span>
                {scan.detail && (scan.state === 'PARTIAL' || scan.state === 'FAILED') && <span className="mt-1 block text-[12px] text-ink-muted">{scan.detail}</span>}
                {measured && (
                  <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-2">
                    {TABLE_METRICS.map((m) => {
                      const value = scan.values?.[m.key] ?? null;
                      const change = scan.sinceBaseline?.[m.key] ?? null;
                      return (
                        <div key={m.key} className="min-w-0">
                          <dt className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-muted">{m.short}</dt>
                          <dd className="text-navy">
                            {value === null ? (
                              <span className="text-[12px] italic text-ink-muted">Not returned</span>
                            ) : (
                              <>
                                {formatMeasure(value, m.unit)}
                                {change !== null && <span className="ml-1.5 text-[11.5px] text-ink-muted">{signed(change, m.unit)}</span>}
                              </>
                            )}
                          </dd>
                        </div>
                      );
                    })}
                  </dl>
                )}
                {index === row.scans.length - 1 && reminder && <span className="mt-1 block text-[12px] text-ink-muted">{reminder}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
